import React from 'react';
import * as DialogRadix from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Keyboard } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Shortcut {
  keys: string[];
  description: string;
  category: string;
}

const SHORTCUTS: Shortcut[] = [
  { keys: ['Ctrl/⌘', 'S'], description: '즉시 저장', category: '편집' },
  { keys: ['Ctrl/⌘', 'Shift', 'S'], description: '다른 이름으로 저장', category: '편집' },
  { keys: ['Ctrl/⌘', 'Z'], description: '실행 취소', category: '편집' },
  { keys: ['Ctrl/⌘', 'Y'], description: '다시 실행', category: '편집' },
  { keys: ['Ctrl/⌘', 'Shift', 'Z'], description: '다시 실행 (대체)', category: '편집' },
  { keys: ['Ctrl/⌘', 'E'], description: '내보내기 페이지 이동', category: '편집' },
  { keys: ['1', '~', '6'], description: '인쇄부 1-6 선택', category: '탐색' },
  { keys: ['['], description: '이전 인쇄부', category: '탐색' },
  { keys: [']'], description: '다음 인쇄부', category: '탐색' },
  { keys: ['Ctrl/⌘', 'Wheel'], description: '캔버스 확대/축소', category: '캔버스' },
  { keys: ['더블클릭'], description: '캔버스 2x 확대/복귀 토글', category: '캔버스' },
  { keys: ['드래그'], description: '확대 상태에서 이동 (pan)', category: '캔버스' },
  { keys: ['?'], description: '이 도움말 열기', category: '기타' },
  { keys: ['Esc'], description: '모달/다이얼로그 닫기', category: '기타' },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const ShortcutsHelp: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const categories = Array.from(new Set(SHORTCUTS.map((s) => s.category)));

  return (
    <DialogRadix.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AnimatePresence>
        {isOpen && (
          <DialogRadix.Portal forceMount>
            <DialogRadix.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 z-[70]"
              />
            </DialogRadix.Overlay>
            <DialogRadix.Content asChild>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[71] w-[90vw] max-w-lg bg-white rounded-xl shadow-2xl p-6"
              >
                <div className="flex justify-between items-center mb-4">
                  <DialogRadix.Title className="flex items-center gap-2 font-semibold text-lg">
                    <Keyboard className="h-5 w-5" />
                    {t('shortcuts.title')}
                  </DialogRadix.Title>
                  <DialogRadix.Close asChild>
                    <button aria-label={t('common.close')} className="p-1 hover:bg-gray-100 rounded">
                      <X className="h-4 w-4" />
                    </button>
                  </DialogRadix.Close>
                </div>
                <div className="space-y-4">
                  {categories.map((cat) => (
                    <div key={cat}>
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        {cat}
                      </h3>
                      <ul className="space-y-1.5">
                        {SHORTCUTS.filter((s) => s.category === cat).map((s, idx) => (
                          <li key={idx} className="flex justify-between items-center text-sm">
                            <span className="text-gray-700">{s.description}</span>
                            <div className="flex gap-1">
                              {s.keys.map((k, ki) => (
                                <kbd
                                  key={ki}
                                  className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono"
                                >
                                  {k}
                                </kbd>
                              ))}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <p className="mt-5 text-xs text-gray-500 text-center">{t('shortcuts.hint')}</p>
              </motion.div>
            </DialogRadix.Content>
          </DialogRadix.Portal>
        )}
      </AnimatePresence>
    </DialogRadix.Root>
  );
};
